/**
 * THE ONE COMMIT POINT for display changes (C4 of docs/display-contract.md).
 *
 * Before this class, atomicity was an EMERGENT property of call order inside VoxelWorld.update():
 * the walk applied flips one chunk at a time mid-loop, evictions landed in a different section,
 * and the stitcher flushed later — all in the same frame TODAY, but nothing made that a rule, and
 * every regression in the week before the overhaul was some mechanism drifting out of that
 * accidental alignment. This class makes the alignment structural: the walk only DECIDES
 * (a Map of desired visibilities), and commit() is the single place pixels change —
 * flips, their skirt consequences, and the audit, in one call.
 *
 * TRANSACTIONS. Flips are grouped along ANCESTRY: a flip joins the transaction of any ancestor
 * decided the other way this pass, at any depth (a refine = show the descendants + hide the
 * ancestor; a coarsen = the reverse; anything else is a singleton). Any depth, not just the direct
 * parent, so a deferred pass is still coverage-safe: a hide never lands without the shows that
 * replace its volume, and shows never land without the hide of what they replace. A transaction
 * either applies WHOLE or not at all:
 *  - Shows apply first. A show that fails (no geometry registered in the batch) rolls back the
 *    transaction's other shows and vetoes its hides — the parent keeps drawing, which is C1-clean
 *    (single coverage preserved; the swap simply hasn't happened yet). The walk re-derives the
 *    same decision next pass, so a vetoed transaction needs no carried state — only a re-arm, and
 *    it asks for that itself the first commit a given key refuses.
 *  - Hides cannot fail (phase 3 made them same-call index-range writes), so a transaction that
 *    gets past its shows always completes.
 *
 * BUDGET. Transactions apply nearest-first until the frame's `budgetEnd` (the ONE clock every voxel
 * consumer shares — voxel/frameBudget.ts), with AT LEAST ONE admitted per commit; the rest are deferred — not applied at all, never half-applied — and `onDeferred` re-arms
 * a pass so they are recomputed while the world is otherwise quiet. The budget bounds a commit's
 * cost without ever splitting a transaction (C4: "a transaction that does not fit this frame's
 * budget is simply NOT APPLIED").
 *
 * SKIRTS ride the same commit: flushSeams() runs after the flips, inside commit(), so a chunk and
 * its stitching land in the same frame — and the stitcher's own `unresolved` retry set (phase 4's
 * other half) keeps any skirt it could not build owed and retried on every subsequent commit.
 * Evictions are applied by the caller BEFORE commit() (removeChunk stops the chunk's pixels in the
 * evicting call since phase 3, and onRemoved enqueues the seam consequences) — this class's flush
 * then settles those consequences in the same frame, which is what C4 asks of evictions.
 */

import { MAX_PACKABLE_LOD, chunkKeyLod, parseChunkKey } from '@worldify/shared';

export interface DisplayCommitDeps {
  /** Apply one visibility flip; returns whether the chunk now HAS that state applied (batch
   *  truth). A false on a SHOW is a veto: the mesh cannot draw this frame. */
  applyFlip(key: string, visible: boolean): boolean;
  /** Fired for every flip that actually changed state (the stitcher's display trigger). */
  onFlipApplied(key: string): void;
  /** The cross-LOD/seam settlement for this commit (SeamStitcher.flush). `budgetEnd` is the frame's
   *  shared clock; the per-flush skirt build loop stops at it (priority owners excepted). */
  flushSeams(budgetEnd: number): void;
  /** Squared distance from the player to a chunk's centre — transaction admission order. */
  distanceSq(key: string): number;
  /** A transaction was deferred: another pass must run so it is recomputed. */
  onDeferred(reason: string): void;
  /** Injected clock so tests can force deferral deterministically. */
  now?(): number;
  /**
   * The skirt owners a flip of `key` makes responsible (SeamStitcher.priorityOwnersFor). Those
   * owners are built in this commit's flush REGARDLESS of budget — a shown chunk gets its seam the
   * same frame — so they are the commit's real cost, and admission is charged for them against the
   * frame clock at `ownerCostMs` each. Optional: without both, a transaction is charged nothing
   * beyond its flips.
   */
  flipOwners?(key: string): Iterable<string>;
  /** Smoothed cost of building one exempt owner's skirts, ms (SeamStitcher.estimatedOwnerCostMs). */
  ownerCostMs?(): number;
}

export interface CommitStats {
  transactions: number;
  applied: number;
  deferred: number;
  vetoed: number;
  flips: number;
  /** Distinct skirt owners the applied flips handed to the flush, and the most one transaction named. */
  owners: number;
  maxTxOwners: number;
}

interface Transaction {
  /** All flips of this transaction, shows before hides. */
  shows: string[];
  hides: string[];
  /** Nearest member — admission order. */
  distSq: number;
}

export class DisplayCommit {
  /** Shows refused by the last commit — the previous state a new veto is measured against. */
  private vetoed: Set<string> = new Set();
  private readonly now: () => number;

  constructor(private readonly deps: DisplayCommitDeps) {
    this.now = deps.now ?? (() => performance.now());
  }

  /**
   * Apply this pass's display decisions. `decisions` maps chunk key -> desired visibility and must
   * contain only CHANGES (the walk filters no-ops); it is consumed whole — deferred transactions
   * are simply absent from the applied set and will be re-decided by the next walk pass.
   */
  commit(decisions: ReadonlyMap<string, boolean>, budgetEnd: number): CommitStats {
    const stats: CommitStats = {
      transactions: 0, applied: 0, deferred: 0, vetoed: 0, flips: 0, owners: 0, maxTxOwners: 0,
    };
    const nowVetoed = new Set<string>();

    if (decisions.size > 0) {
      const txs = this.groupIntoTransactions(decisions);
      stats.transactions = txs.length;
      txs.sort((a, b) => a.distSq - b.distSq);

      const owners = new Set<string>();
      for (let i = 0; i < txs.length; i++) {
        // >= 1 admitted per commit, then the FRAME's clock gates (one budget for all voxel work —
        // see frameBudget.ts; this used to be a private 3 ms inside a 2 ms display pass): a deferred
        // transaction is recomputed by the next pass from fresh decisions, never carried half-done.
        if (stats.applied > 0 && this.now() >= budgetEnd) {
          stats.deferred = txs.length - i;
          this.deps.onDeferred('commit-budget');
          break;
        }
        const tx = txs[i];
        // The owners this transaction's flips would hand to the flush. Owners are DISTINCT because
        // the fine chunks of one coarse cell name the same neighbour.
        const txOwners = new Set<string>();
        if (this.deps.flipOwners) {
          for (const key of tx.shows) for (const o of this.deps.flipOwners(key)) txOwners.add(o);
          for (const key of tx.hides) for (const o of this.deps.flipOwners(key)) txOwners.add(o);
        }
        // ...and the SKIRT charge: those owners are built regardless of budget. The flips themselves
        // are index writes, so the time gate alone admitted a whole arriving ring and the flush then
        // ran 18-40 ms. Predicted before applying — every distinct owner admitted so far, at the
        // stitcher's measured cost — so a transaction whose skirts would not fit the frame is deferred
        // whole, same shape as the time gate.
        if (stats.applied > 0 && this.deps.flipOwners && this.deps.ownerCostMs) {
          let pending = 0;
          for (const o of txOwners) if (!owners.has(o)) pending++;
          const predicted = (owners.size + pending) * this.deps.ownerCostMs();
          if (this.now() + predicted >= budgetEnd) {
            stats.deferred = txs.length - i;
            this.deps.onDeferred('skirt-budget');
            break;
          }
        }
        const done: string[] = [];
        let refused: string | null = null;
        for (const key of tx.shows) {
          if (this.deps.applyFlip(key, true)) { done.push(key); continue; }
          // A show the apply layer cannot honor vetoes the WHOLE transaction: roll the applied
          // shows back and leave the hides untouched — the replaced set keeps drawing (C1-clean).
          for (const k of done) this.deps.applyFlip(k, false);
          refused = key;
          break;
        }
        if (refused !== null) {
          // A VETO ARMS ITS OWN RETRY, ONCE PER NEW REFUSING KEY. The decisions map is consumed
          // whole, so the refused show only comes back if some pass re-derives it — and "the
          // arriving mesh dirties a pass" is not a guarantee: an apply layer with no slot for the
          // key is refusing for reasons no mesh event will announce, and that transaction then
          // waited on unrelated churn. Armed on the CHANGE rather than on every veto, because
          // re-arming while the same key keeps refusing is the live pump the old rule feared (the
          // walk every frame for as long as the mesh streams).
          nowVetoed.add(refused);
          stats.vetoed++;
          continue;
        }
        for (const key of tx.hides) {
          if (this.deps.applyFlip(key, false)) done.push(key);
        }
        stats.applied++;
        stats.flips += done.length;
        if (txOwners.size > stats.maxTxOwners) stats.maxTxOwners = txOwners.size;
        if (this.deps.flipOwners) for (const key of done) for (const o of this.deps.flipOwners(key)) owners.add(o);
        stats.owners = owners.size;
        for (const key of done) this.deps.onFlipApplied(key);
      }
    }

    let fresh = false;
    for (const key of nowVetoed) if (!this.vetoed.has(key)) { fresh = true; break; }
    this.vetoed = nowVetoed;
    if (fresh) this.deps.onDeferred('commit-veto');

    // Skirt consequences settle in the SAME commit — including retries the stitcher still owes
    // from earlier commits (its unresolved set), which is why this runs even with zero flips.
    // The shared `budgetEnd` (when provided) is the wall-clock end of the whole display pass —
    // see VoxelWorld.update for where it is set. Pass it through so the stitcher competes for the
    // same clock as the batch compaction that follows, instead of having its own independent budget.
    this.deps.flushSeams(budgetEnd);

    return stats;
  }

  /**
   * Ancestry grouping. A flip joins the transaction of any ancestor (up to MAX_PACKABLE_LOD levels
   * up) decided the OPPOSITE way this pass; union-find so grandparent↔grandchildren and mixed chains
   * land as one transaction. Every transaction is then coverage-sound on its own, which is what lets
   * the admission gates defer some and apply others without a hole or a double draw between them.
   */
  private groupIntoTransactions(decisions: ReadonlyMap<string, boolean>): Transaction[] {
    const keys = [...decisions.keys()];
    const index = new Map<string, number>();
    for (let i = 0; i < keys.length; i++) index.set(keys[i], i);
    const parent = keys.map((_, i) => i);
    const find = (i: number): number => {
      while (parent[i] !== i) { parent[i] = parent[parent[i]]; i = parent[i]; }
      return i;
    };
    for (let i = 0; i < keys.length; i++) {
      const show = decisions.get(keys[i])!;
      let { cx, cy, cz } = parseChunkKey(keys[i]);
      const lod = parseChunkKey(keys[i]).lod;
      for (let l = lod + 1; l <= MAX_PACKABLE_LOD; l++) {
        cx >>= 1; cy >>= 1; cz >>= 1;
        const j = index.get(chunkKeyLod(cx, cy, cz, l));
        if (j === undefined || decisions.get(keys[j]) === show) continue;
        const a = find(i), b = find(j);
        if (a !== b) parent[a] = b;
      }
    }
    const byRoot = new Map<number, Transaction>();
    for (let i = 0; i < keys.length; i++) {
      const root = find(i);
      let tx = byRoot.get(root);
      if (!tx) { tx = { shows: [], hides: [], distSq: Infinity }; byRoot.set(root, tx); }
      const key = keys[i];
      (decisions.get(key) ? tx.shows : tx.hides).push(key);
      const d = this.deps.distanceSq(key);
      if (d < tx.distSq) tx.distSq = d;
    }
    return [...byRoot.values()];
  }

}
